import BtnExcelDownload from "@/components/button/BtnExcelDownload";
import PageLayout from "@/components/layout/PageLayout";
import { useDepartmentHeadsSearch } from "@/services/userManageService";
import { SearchQuery } from "@/types/common";
import { UserSearchCondition } from "@/types/user";
import { Close as CloseIcon, Inbox as InboxIcon } from "@mui/icons-material";
import { Box, Button, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";

const DepartmentHeadsListPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<SearchQuery<UserSearchCondition>>({ page: 0, size: 25 });
  const [localSearch, setLocalSearch] = useState<UserSearchCondition>({ roleId: null, keyword: "" });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchQuery.page,
    pageSize: searchQuery.size,
  });

  const { data, isFetching } = useDepartmentHeadsSearch(searchQuery);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (data?.items) {
      setRows(
        data.items.map((row) => ({
          ...row,
          id: row.id ?? "",
          roleId: row.roleId ?? "",
        }))
      );
    }
  }, [data?.items]);

  useEffect(() => {
    setSearchQuery((prev) => {
      if (prev.page === paginationModel.page && prev.size === paginationModel.pageSize) {
        return prev;
      }
      return { ...prev, page: paginationModel.page, size: paginationModel.pageSize };
    });
  }, [paginationModel]);

  useEffect(() => {
    setPaginationModel({ page: searchQuery.page, pageSize: searchQuery.size });
  }, [searchQuery.page, searchQuery.size]);

  const handleSortingModelChange = (model: GridSortModel) => {
    if (model.length === 0) return;
    const sort = model[0];
    setSearchQuery((prev) => {
      if (prev.sortBy === sort.field && prev.direction === (sort.sort === "asc" ? "ASC" : "DESC")) return prev;
      return {
        ...prev,
        page: 0,
        sortBy: sort.field,
        direction: sort.sort === "asc" ? "ASC" : "DESC",
      };
    });
  };

  const columns: GridColDef[] = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const rowIndex = params.api.getAllRowIds().indexOf(params.id);
        const totalCount = data?.pageInfo.total ?? 0;
        return totalCount - (searchQuery.page * searchQuery.size + rowIndex);
      },
    },
    { field: "name", headerName: "이름", headerAlign: "center", minWidth: 150 },
    { field: "accountId", headerName: "계정 아이디", headerAlign: "center", minWidth: 200 },
    {
      field: "email",
      headerName: "이메일",
      headerAlign: "center",
      minWidth: 300,
    },
    { field: "affiliatedDepartments", headerName: "소속부서", headerAlign: "center", sortable: false, flex: 1 },
    { field: "createdDate", headerName: "등록일", headerAlign: "center", align: "center", minWidth: 150 },
  ];

  const handleSearchButtonClick: () => void = () => {
    if (localSearch.keyword && (localSearch.keyword.length < 2 || localSearch.keyword.length > 20)) {
      alert("통합 검색어는 최소 2자이상 20자이하로 입력해주세요.");
      return;
    }

    setSearchQuery((prev) => ({
      ...prev,
      page: 0, // 검색 시 페이지 초기화
      conditions: { ...localSearch, roleId: 0 === Number(localSearch.roleId) ? null : localSearch.roleId },
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  return (
    <PageLayout title='조직장 확인'>
      <Box sx={{ mb: 3 }}>
        {/* 검색 영역 */}
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 2,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <TextField
                placeholder='통합 검색어를 입력하세요(최소 2자이상 20자이하)'
                label='통합 검색'
                size='small'
                sx={{ flex: 1 }}
                value={localSearch.keyword ?? ""}
                onChange={(e) => setLocalSearch((prev) => ({ ...prev, keyword: e.target.value }))}
                onKeyDown={handleKeyPress}
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        {localSearch.keyword && (
                          <InputAdornment position='end'>
                            <IconButton
                              size='small'
                              onClick={() => setLocalSearch((prev) => ({ ...prev, keyword: "" }))}
                              edge='end'
                              tabIndex={-1}
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </InputAdornment>
                        )}
                        <InputAdornment position='end'>
                          <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                            {`${localSearch.keyword?.length ?? 0}/20 자`}
                          </span>
                        </InputAdornment>
                      </>
                    ),
                  },
                }}
              />
              <Button
                variant='contained'
                sx={{ width: 100, height: 40, ml: 1, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                onClick={handleSearchButtonClick}
              >
                검색
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        {/* 버튼 영역 */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <BtnExcelDownload
            disabled={!data?.items.length}
            key='excel'
            apiUrl={`/approver/department-heads/excel-export`}
            searchQuery={searchQuery}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          rowCount={data?.pageInfo.total ?? 0}
          disableRowSelectionOnClick
          disableColumnMenu
          rowHeight={48}
          loading={isFetching}
          pageSizeOptions={[25, 50, 100]}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          sortingMode='server'
          onSortModelChange={handleSortingModelChange}
          slots={{
            noRowsOverlay: () => (
              <GridOverlay>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    fontSize: "18px",
                    color: "#888",
                    gap: "8px",
                  }}
                >
                  <InboxIcon sx={{ fontSize: 40, color: "#888" }} />
                  표시할 데이터가 없습니다.
                </div>
              </GridOverlay>
            ),
          }}
          sx={{
            // DataGrid 내부 스크롤만 돌도록
            "& .MuiDataGrid-main": { overflowY: "auto" },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell--editing": {
              outline: "none !important",
              boxShadow: "none !important",
            },
            "& .MuiInputBase-root": {
              boxShadow: "none !important",
            },
            "& .MuiDataGrid-checkboxInput .MuiSvgIcon-root": {
              fontSize: 20,
            },
          }}
        />
      </Box>
    </PageLayout>
  );
};

export default DepartmentHeadsListPage;
